/**
 * Infrastructure Layer - GoogleSheetsExporterImpl
 * Google Sheetsエクスポートポートの実装
 */

import type {
  SpreadsheetExporter,
  ExportRequest,
} from '../../application/medicalReservation/ports/SpreadsheetExporter';
import type { OutsideTimeExportResult } from '../../domain/medicalReservation/OutsideTimeExportResult';
import { createSuccessExportResult, createFailureExportResult } from '../../domain/medicalReservation/OutsideTimeExportResult';
import {
  ExportErrorType,
  type ExportError,
  type Result,
  ok,
  err,
  createExportError,
} from '../../domain/medicalReservation/errors';
import {
  exportOutsideTimeRangeData,
  ExportErrorCode,
  type ExportResponse,
} from '../api/googleSheetsApi';

/**
 * APIエラーコードをドメインエラータイプに変換する
 */
const convertErrorCode = (code: string): ExportErrorType => {
  switch (code) {
    case ExportErrorCode.SPREADSHEET_NOT_FOUND:
      return ExportErrorType.SPREADSHEET_NOT_FOUND;
    case ExportErrorCode.PERMISSION_DENIED:
      return ExportErrorType.PERMISSION_DENIED;
    case ExportErrorCode.RATE_LIMIT_EXCEEDED:
      return ExportErrorType.QUOTA_EXCEEDED;
    default:
      return ExportErrorType.API_ERROR;
  }
};

/**
 * APIレスポンスからOutsideTimeExportResultに変換する
 */
const convertToExportResult = (
  response: ExportResponse
): OutsideTimeExportResult => {
  if (response.success && response.data) {
    return createSuccessExportResult(
      response.data.sheet_name,
      response.data.spreadsheet_url,
      response.data.rows_written,
      {
        sheetId: response.data.sheet_id,
        writeStartRow: response.data.write_start_row,
        timeRange: response.data.time_range,
      }
    );
  }

  return createFailureExportResult(
    response.error?.message ?? 'エクスポートに失敗しました',
    response.error?.code
  );
};

/**
 * Google Sheetsエクスポートポートの実装
 */
export class GoogleSheetsExporterImpl implements SpreadsheetExporter {
  /**
   * データをスプレッドシートにエクスポートする
   */
  async export(
    request: ExportRequest
  ): Promise<Result<OutsideTimeExportResult, ExportError>> {
    try {
      // メタデータを変換
      const metadata = request.metadata
        ? {
            original_filename: request.metadata.originalFilename,
            total_records: request.metadata.totalRecords,
            outside_records: request.metadata.outsideRecords,
            time_range: request.metadata.timeRange
              ? {
                  start: request.metadata.timeRange.start,
                  end: request.metadata.timeRange.end,
                }
              : undefined,
          }
        : undefined;

      // APIを呼び出してエクスポート
      const response = await exportOutsideTimeRangeData(
        request.data as Record<string, string>[],
        metadata,
        request.debug
      );

      // レスポンスを変換
      const result = convertToExportResult(response);

      if (result.success) {
        return ok(result);
      } else {
        // エラータイプを判定
        const errorType = response.error?.code
          ? convertErrorCode(response.error.code)
          : ExportErrorType.API_ERROR;

        return err(
          createExportError(
            errorType,
            result.error ?? 'エクスポートに失敗しました',
            undefined,
            response.error?.code
          )
        );
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Google Sheetsへのエクスポートに失敗しました';

      // エラータイプを判定
      let errorType: ExportErrorType = ExportErrorType.API_ERROR;
      if (message.includes('auth') || message.includes('認証')) {
        errorType = ExportErrorType.AUTH_ERROR;
      } else if (message.includes('quota') || message.includes('制限')) {
        errorType = ExportErrorType.QUOTA_EXCEEDED;
      }

      return err(
        createExportError(
          errorType,
          message,
          error instanceof Error ? error.stack : undefined
        )
      );
    }
  }
}

/**
 * GoogleSheetsExporterImplのファクトリ関数
 */
export const createGoogleSheetsExporter = (): SpreadsheetExporter => {
  return new GoogleSheetsExporterImpl();
};
